export interface Volume {
  icon: string
  number: string
  title: string
  subtitle: string
  problem: string
  bullets: string[]
  result: string
  popular?: boolean
}

export const volumes: Volume[] = [
  {
    icon: '🌱',
    number: 'System 1 — Foundation & Fundamentals',
    title: 'Mindfulness Foundations Training System',
    subtitle:
      'Everything a new practitioner needs to start building a real practice — safely and sustainably',
    popular: true,
    problem:
      'Teaching mindfulness to beginners is rewarding and demanding. This system gives you the structure to guide new practitioners with clarity, patience, and logical progression.',
    bullets: [
      'Safe, accessible introduction to mindfulness',
      'Posture, breathing, and basic body awareness',
      'Simple and progressive attention anchors',
      'Grounding practices for beginners',
      'Ready-to-use cueing and language',
      'Mini-assessments to track initial progress',
      'Adaptations for different ages and contexts',
    ],
    result:
      'Your beginners feel supported, capable, and motivated to return from their first class.',
  },
  {
    icon: '🕊️',
    number: 'System 2 — Body & Sensory Awareness',
    title: 'Complete Body Awareness Teaching System',
    subtitle:
      'Structured sequences of body awareness practices — organized, progressive, and ready to teach',
    problem:
      'Body awareness is the foundation of all mindfulness practices, but building new progressions every week is exhausting. This system keeps every class intentional and connected to the one before it.',
    bullets: [
      'Complete body scans with minute-by-minute timing',
      'Progressive sensory awareness sequences',
      'Grounding through contact points',
      'Progressive body-part awareness',
      'Ready-to-use cues and corrections',
      'Chair-based and alternative position adaptations',
      'Clear weekly progressions and transitions',
    ],
    result:
      'Your body awareness work feels technically solid and progressive, and students develop real presence and grounding every single week.',
  },
  {
    icon: '🎯',
    number: 'System 3 — Attention & Focus Mastery',
    title: 'Advanced Attention Training System',
    subtitle:
      'Movement from distraction toward focus — completely guided from first thought to calm return',
    problem:
      'Attention practice requires precise sequencing, clear cueing, and understanding of the mind. This system eliminates the planning burden with attention classes that flow naturally.',
    bullets: [
      'Focused attention practice with different anchors',
      'Attention training from simple to complex',
      'Distraction management and gentle return',
      'Breath as attention anchor',
      'Sound and sensation as alternative anchors',
      'Cueing and language to maintain focus',
      'Technical corrections and troubleshooting',
    ],
    result: 'Your attention classes feel polished, intentional, and deeply satisfying every time.',
  },
  {
    icon: '💗',
    number: 'System 4 — Open Awareness & Emotional Regulation',
    title: 'Complete Open Awareness & Emotional Mastery System',
    subtitle: 'The sequences that transform students most — and the most difficult to guide skillfully',
    problem:
      'Open awareness practice requires sensitivity, clear organization, and gradual cue reduction. These ready-to-teach sessions mean you will never improvise the transition from focus to openness again.',
    bullets: [
      'Transitions from focused attention to openness',
      'Observing thoughts without reactivity',
      'Silence architecture and cue fading',
      'Gentle re-entry and safe closing',
      'Emotional regulation through awareness',
      'Compassion and loving-kindness practices',
      'Language for heart-centered practice',
    ],
    result:
      'Your open awareness and compassion practices feel organized, profound, and deeply transformative — and practitioners build real capacity for presence and emotional balance.',
  },
  {
    icon: '🧭',
    number: 'System 5 — Long-Form Facilitation & Advanced Teaching',
    title: 'Advanced Long-Form Facilitation & Script Library System',
    subtitle: 'Complete sessions ready for every context, age group, and teaching situation',
    problem:
      'Mixed-ability groups, emergency sessions, retreats, and longer sessions are usually the most difficult to facilitate. This system guarantees you always have the right session for the room.',
    bullets: [
      'Long-form sessions (45, 60, 75, 90 minutes) fully structured',
      'Silence architecture for extended practice',
      'Advanced facilitation and observation',
      'Mixed-ability, emergency, and low-prep sessions',
      'Retreat and immersive experiences',
      'Online, private, group and corporate contexts',
      'Chair-based, teen and older adult adaptations',
    ],
    result: 'You have the right session for every teaching moment, without building anything from scratch.',
  },
  {
    icon: '🌿',
    number: 'System 6 — Student Engagement & Business Growth',
    title: 'Complete Student Retention, Progress & Engagement System',
    subtitle:
      'Tools to keep students practicing long after class ends and build a sustainable teaching practice',
    problem:
      'Many students practice in class but not at home. This system provides structures so they develop real independence and build a genuine personal practice — while you grow a thriving teaching business.',
    bullets: [
      'Home practice guides and tracking',
      'Student progress assessments and tracking sheets',
      'Between-session engagement tools and reflection prompts',
      'Private session structures',
      'Program offers and pricing guidance',
      'Workshop and retreat planning',
      'Professional packages and recurring programs',
    ],
    result:
      'Your students develop a genuine personal practice, you maintain engagement, and you build a thriving, professional teaching practice.',
  },
]
